import { Injectable, Inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BaseHttpService, silentErrorStatuses } from '../http/base.http.service';
import { ApiRouterService } from '../http/api.router';
import { RouteApiRoutes } from './route-api.routes';
import { objectToSnake, toSnake } from 'ts-case-convert';
import { IProcessingOptions, IRestrictions, IRouteAlteration, IRouteDetails, IRouteDraft, IRouteScheduledUpdate, IRouteSetupDetail } from 'src/app/models';
import { ExpeditionStatus, HttpResponseType, RestrictionsType } from 'src/app/models/enums';
import { UtilsService } from '../utils/utils.service';
import { clearDisabledToggleDependents } from 'src/app/store/initialisation/setup';

@Injectable({
  providedIn: 'root',
})
export class RouteHttpService extends BaseHttpService {
  private baseURL = `services`;

  constructor(
    @Inject(HttpClient) httpClient: HttpClient,
    private routerService: ApiRouterService,
    private utilsService: UtilsService,
  ) {
    super(httpClient);
  }

  public getServices(page: number, size: number) {
    const url = this.routerService.mount(`${this.baseURL}?page=${page}&size=${size}`);
    return this.read(url);
  }

  public getService(idService: number, includeTownStops = false) {
    const url = this.routerService.mount(`${this.baseURL}/${idService}${includeTownStops ? '?town_stops=true' : ''}`);
    return this.read(url);
  }

  public getServiceConfig(idService: number) {
    const url = this.routerService.mount(`${this.baseURL}/${idService}/config`);
    return this.read(url);
  }

  public getAvailablities(serviceId: number, page?: number, size?: number, availabilityId?: number) {
    const availabilityIdString = availabilityId ? `&availability_id=${availabilityId}` : '';
    let url = this.routerService.mount(`${this.baseURL}/${serviceId}/availabilities?page=${page}&size=${size}${availabilityIdString}`);
    return this.read(url);
  }

  public getAvailablityStops(availabilityId: number, page: number, size: number, inStopId?: number) {
    const inStopIdString = inStopId ? `&in_stop_id=${inStopId}` : '';
    const url = this.routerService.mount(`${this.baseURL}/${availabilityId}/availability-stops?page=${page}&size=${size}${inStopIdString}`);
    return this.read(url);
  }

  public importServiceFromGtfs(data: any, clientId: number, snapToRoad: boolean, townId?: number, townName?: string, appIds?: number[]) {
    let url = this.routerService.mount(`${this.baseURL}/${RouteApiRoutes.IMPORT_GTFS}?client_id=${clientId}&compute_routes=${snapToRoad}`);
    url += townId ? `&town_id=${townId}` : '';
    url += townName ? `&town_name=${townName}` : '';
    url += appIds && appIds.length > 0 ? `&app_ids=${appIds}` : '';
    const formData: FormData = new FormData();
    data.forEach((element: any) => {
      formData.append(toSnake(element.type), data.find((u: any) => u.type === element.type)!.file.value);
    });
    return this.createMultipart(url, formData);
  }

  public importServiceFromExcel(data: any, clientId: number, snapToRoad: boolean, townId?: number, townName?: string) {
    let url = this.routerService.mount(`${this.baseURL}/${RouteApiRoutes.IMPORT_EXCEL}?client_id=${clientId}&compute_routes=${snapToRoad}`);
    url += townId ? `&town_id=${townId}` : '';
    url += townName ? `&town_name=${townName}` : '';
    const formData: FormData = new FormData();
    formData.append('services', data)
    return this.createMultipart(url, formData, HttpResponseType.TEXT);
  }

  public downloadGtfs(routeId: number) {
    let url = this.routerService.mount(`${this.baseURL}/${routeId}/${RouteApiRoutes.DOWNLOAD_GTFS}`);
    return this.readZip(url);
  }
  
  public getRoutesByServiceInfo(serviceIds: number[], page?: number, size?: number) {
    let url = this.routerService.mount(`${this.baseURL}?service_infos=${serviceIds}`);
    url += page ? `&page=${page}` : '';
    url += size ? `&size=${size}` : '';
    return this.read(url);
  }

  public getRoutesSearch(serviceIds: number[], page?: number, size?: number, searchValue?: string) {
    let url = this.routerService.mount(`${this.baseURL}/filter?service_info=${serviceIds}`);
    url += page ? `&page=${page}` : '';
    url += size ? `&size=${size}` : '';
    url += searchValue !== undefined ? `&search=${encodeURIComponent(searchValue)}` : '';
    return this.read(url);
  }

  public getRelateds(serviceId: number) {
    let url = this.routerService.mount(`${this.baseURL}/${serviceId}/related`);
    return this.read(url);
  }

  private processingOptionsQuery(options?: any): string {
    if (!options) return '';
    const params: string[] = [];
    if (options?.rebuild !== undefined) params.push(`rebuild=${options.rebuild}`);
    if (options?.rebuildStatus && options.rebuildStatus.length > 0) params.push(`rebuildStatus=${options.rebuildStatus}`);
    if (options?.sendNotification !== undefined) params.push(`sendNotification=${options.sendNotification}`);
    if (options?.refund !== undefined) params.push(`refund=${options.refund}`);
    if (options?.cancelRelated !== undefined) params.push(`cancelRelated=${options.cancelRelated}`);
    if (options?.fullRefund !== undefined) params.push(`fullRefund=${options.fullRefund}`);
    if (options?.skipTripsIds && options.skipTripsIds.length > 0) params.push(`skipTripsIds=${options.skipTripsIds}`);
    if (options?.townStopsIncluded) params.push('town_stops=true');
    return params.length > 0 ? `?${params.join('&')}` : '';
  }

  public updateRoute(route: IRouteDetails, options?: any, townStopsIncluded = false) {
    const url = this.routerService.mount(`${this.baseURL}/async/${route.id}`) + this.processingOptionsQuery({ ...(options ?? {}), townStopsIncluded });
    return this.update(this.utilsService.snakeCaseBody(route), url, HttpResponseType.TEXT);
  }

  public getRouteDraftTerrain(routeId: number) {
    const url = this.routerService.mount(`${this.baseURL}/${routeId}/${RouteApiRoutes.DRAFT_TERRAIN}`);
    return this.read(url);
  }

  public saveRouteDraftTerrain(routeId: number, draft: IRouteDraft) {
    const url = this.routerService.mount(`${this.baseURL}/${routeId}/${RouteApiRoutes.DRAFT_TERRAIN}`);
    return this.update(this.utilsService.snakeCaseBody(draft.terrain ?? {}), url);
  }

  public applyRouteDraftTerrain(routeId: number, options?: IProcessingOptions) {
    const url = this.routerService.mount(`${this.baseURL}/${routeId}/${RouteApiRoutes.APPLY_DRAFT_TERRAIN}`) + this.processingOptionsQuery(options);
    return this.createText({}, url);
  }

  public getRouteScheduledChange(routeId: number) {
    const url = this.routerService.mount(`${this.baseURL}/${routeId}/${RouteApiRoutes.SCHEDULED_CHANGE}`);
    return this.read(url, silentErrorStatuses(404));
  }

  public scheduleRouteTerrainUpdate(routeId: number, scheduledUpdate: IRouteScheduledUpdate, processingOptions?: IProcessingOptions) {
    const url = this.routerService.mount(`${this.baseURL}/${routeId}/${RouteApiRoutes.SCHEDULE_TERRAIN_UPDATE}`);
    const body = {
      terrain: scheduledUpdate.terrain,
      dateTime: scheduledUpdate.dateTime,
      rebuildTrips: !!processingOptions?.rebuild,
      rebuildStatus: processingOptions?.rebuildStatus,
      sendNotification: !!processingOptions?.sendNotification,
      refund: !!processingOptions?.refund,
      cancelRelated: !!processingOptions?.cancelRelated,
      fullRefund: !!processingOptions?.fullRefund,
      skipTripsIds: processingOptions?.skipTripsIds || []
    }
    return this.create(this.utilsService.snakeCaseBody(body), url);
  }

  public deleteRouteScheduledChange(routeId: number) {
    const url = this.routerService.mount(`${this.baseURL}/${routeId}/${RouteApiRoutes.SCHEDULED_CHANGE}`);
    return this.delete(url);
  }

  public createRoute(newRoute: IRouteDetails, townStopsIncluded = false, options?: IProcessingOptions) {
    const url = this.routerService.mount(`${this.baseURL}`) + this.processingOptionsQuery({ ...(options ?? {}), townStopsIncluded });
    return this.create(this.utilsService.snakeCaseBody(newRoute), url);
  }

  public createQuickService(payload: any) {
    const url = this.routerService.mount(`${this.baseURL}/quick-creation/async`);
    return this.createText(this.utilsService.snakeCaseBody(payload), url);
  }

  public getRouteTerrains(routeId: number) {
    let url = this.routerService.mount(`${this.baseURL}/${routeId}/${RouteApiRoutes.GET_TERRAINS}`);
    return this.read(url);
  }

  public activate(routeId: number) {
    let url = this.routerService.mount(`${this.baseURL}/${routeId}/activate`);
    return this.updatePatch({}, url);
  }

  public deactivate(routeId: number) {
    let url = this.routerService.mount(`${this.baseURL}/${routeId}/deactivate`);
    return this.updatePatch({}, url);
  }

  public deleteRoute(routeId: number) {
    const url = this.routerService.mount(`${this.baseURL}/${routeId}`);
    return this.delete(url);
  }

  public getDefaultSetupConfig() {
    const url = this.routerService.mount(`${this.baseURL}/setups/default-config`);
    return this.read(url);
  }

  public createSetupDepartures(routeId: number, setupId: number, departures: any[]) {
    const url = this.routerService.mount(`${this.baseURL}/${routeId}/setups/${setupId}/departures`);
    const departuresPayload = departures.map((departure: any) => ({
      departureDateTime: departure.departureDateTime,
      vehicleId: departure.vehicleId,
      driverId: departure.driverId,
    }));
    return this.create(objectToSnake({ departures: departuresPayload }), url);
  }

  public getRouteSetups(routeId: number) {
    const url = this.routerService.mount(`${this.baseURL}/${routeId}/setups`);
    return this.read(url);
  }

  public getRouteSetupsSchedule(routeId: number, setupId: number) {
    const url = this.routerService.mount(`${this.baseURL}/${routeId}/setups/${setupId}`);
    return this.read(url);
  }

  public activateSetup(routeId: number, setupId: number) {
    const url = this.routerService.mount(`${this.baseURL}/${routeId}/setups/${setupId}/activate`);
    return this.updatePatch({}, url);
  }

  public deactivateSetup(routeId: number, setupId: number) {
    const url = this.routerService.mount(`${this.baseURL}/${routeId}/setups/${setupId}/deactivate`);
    return this.updatePatch({}, url);
  }

  public updateSetup(routeId: number, setupId: number, routeSetupDetail: IRouteSetupDetail, options?: {
    applyToAvailabilities?: boolean;
    updateTrips?: boolean;
    sendNotification?: boolean;
    deleteOrphanedAvailabilities?: boolean;
    deleteComment?: string;
    refund?: boolean;
    cancelRelated?: boolean;
    fullRefund?: boolean;
  }) {
    let url = this.routerService.mount(`${this.baseURL}/${routeId}/setups/${setupId}`);

    if (options) {
      const params: string[] = [];
      if (options.applyToAvailabilities !== undefined) params.push(`applyToAvailabilities=${options.applyToAvailabilities}`);
      if (options.updateTrips !== undefined) params.push(`updateTrips=${options.updateTrips}`);
      if (options.sendNotification !== undefined) params.push(`sendNotification=${options.sendNotification}`);
      if (options.deleteOrphanedAvailabilities !== undefined) params.push(`deleteOrphanedAvailabilities=${options.deleteOrphanedAvailabilities}`);
      if (options.deleteComment) params.push(`deleteComment=${encodeURIComponent(options.deleteComment)}`);
      if (options.refund !== undefined) params.push(`refund=${options.refund}`);
      if (options.cancelRelated !== undefined) params.push(`cancelRelated=${options.cancelRelated}`);
      if (options.fullRefund !== undefined) params.push(`fullRefund=${options.fullRefund}`);
      if (params.length > 0) url += `?${params.join('&')}`;
    }

    const body = objectToSnake(this.withClearedToggleDependents(routeSetupDetail));
    return this.update(body, url)
  }

  public createSetup(routeId: number, routeSetupDetail: IRouteSetupDetail) {
    const url = this.routerService.mount(`${this.baseURL}/${routeId}/setups`);
    const body = objectToSnake(this.withClearedToggleDependents(routeSetupDetail));
    body.holiday_calendars = body.holiday_calendars?.map((calendar: any) => {
      const { label, ...rest } = calendar;
      return rest;
    });
    return this.create(body, url)
  }

  private withClearedToggleDependents(routeSetupDetail: IRouteSetupDetail): IRouteSetupDetail {
    if (!routeSetupDetail.config) return routeSetupDetail;
    return { ...routeSetupDetail, config: clearDisabledToggleDependents(routeSetupDetail.config) };
  }

  public getTrips(routeId: number, page?: number, size?: number, search?: string) {
    let url = this.routerService.mount(`${this.baseURL}/${routeId}/trips`);
    if (page !== undefined && size !== undefined) {
      url += `?page=${page}&size=${size}`;
    }
    url += search ? `${url.includes('?') ? '&' : '?'}search=${search}` : '';
    return this.read(url);
  }

  public getApps(routeId: number) {
    const url = this.routerService.mount(`${this.baseURL}/${routeId}/apps`);
    return this.read(url);
  }

  public getRouteAvailabilities(routeId: number, page?: number, size?: number, search?: string, status?: ExpeditionStatus[], bookings?: boolean, fromDate?: Date, toDate?: Date, startTime?: string, endTime?: string, setupId?: number) {
    let url = this.routerService.mount(`${this.baseURL}/${routeId}/availabilities?page=${page}&size=${size}`);
    url += search ? `&search=${encodeURIComponent(search)}` : '';
    if (status && status.length > 0) {
      const statusString = status.map(status => status).join(',');
      url += `&status=${statusString}`;
    }    
    url += bookings !== undefined ? `&bookings=${bookings}` : '';
    url += fromDate ? `&from=${fromDate}` : '';
    url += toDate ? `&to=${toDate}` : '';
    url += startTime ? `&start=${startTime}` : '';
    url += endTime ? `&end=${endTime}` : '';
    url += setupId ? `&setup_id=${setupId}` : '';
    return this.read(url);
  }

  public updateAvailabilities(routeId: number, availabilitiesIds: number[], driverId: number, vehicleId: number, updateTrips: boolean, sendNotification: boolean, time?: any, comment?: string) {
    const url = this.routerService.mount(`${this.baseURL}/${routeId}/availabilities`);
    const body = objectToSnake({availabilitiesIds, driverId, vehicleId, time, updateTrips, sendNotification, comment});
    return this.update(body, url)
  }

  public createBackupAvailability(routeId: number, availabilityId: number, driverId: number, vehicleId: number, comment?: string) {
    const url = this.routerService.mount(`${this.baseURL}/${routeId}/availabilities/${availabilityId}/backup`);
    const body = objectToSnake({driverId, vehicleId, comment});
    return this.create(body, url)
  }

  public deleteAvailabilities(routeId: number, availabilitiesIds: number[], comment: string | undefined, sendNotification: boolean, refund: boolean, cancelRelated: boolean, fullRefund: boolean) {
    const url = this.routerService.mount(`${this.baseURL}/${routeId}/availabilities`);
    const body = objectToSnake({availabilitiesIds, comment, sendNotification, refund, cancelRelated, fullRefund});
    return this.delete(url, body)
  }

  public getRoutePreview(route: IRouteDetails, rebuildStatus?: string[], townStopsIncluded = false) {
    let url = this.routerService.mount(route.id ? `${this.baseURL}/${route.id}/preview/async` : `${this.baseURL}/preview/async`);
    const params: string[] = [];
    if (rebuildStatus && rebuildStatus.length > 0) params.push(`rebuild_status=${rebuildStatus}`);
    if (townStopsIncluded) params.push('town_stops=true');
    if (params.length > 0) url += `?${params.join('&')}`;
    return this.createText(this.utilsService.snakeCaseBody(route), url);
  }

  public cloneSetup(routeId: number, setupId: number, name: string) {
    const url = this.routerService.mount(`${this.baseURL}/${routeId}/setups/${setupId}/clone?name=${encodeURIComponent(name)}`);
    return this.create({}, url);
  }
  
  public getRouteAlterations(routeId: number) {
    const url = this.routerService.mount(`${this.baseURL}/${routeId}/alterations`);
    return this.read(url);
  }

  public getAlteration(routeId: number, alterationId: number) {
    const url = this.routerService.mount(`${this.baseURL}/${routeId}/alterations/${alterationId}`);
    return this.read(url);
  }

  public createAlteration(routeId: number, alteration: IRouteAlteration, processingOptions: IProcessingOptions) {
    const url = this.routerService.mount(`${this.baseURL}/${routeId}/alterations`);
    const body = {
      routeAlteration: objectToSnake(alteration),
      processingOptions: objectToSnake(processingOptions)
    }
    return this.createText(this.utilsService.snakeCaseBody(body), url);
  }

  public updateAlteration(routeId: number, alteration: IRouteAlteration, processingOptions: IProcessingOptions) {
    const url = this.routerService.mount(`${this.baseURL}/${routeId}/alterations/${alteration.id}`);
    const body = {
      routeAlteration: objectToSnake(alteration),
      processingOptions: objectToSnake(processingOptions)
    }
    return this.update(this.utilsService.snakeCaseBody(body), url, HttpResponseType.TEXT);
  }

  public getAlterationPreview(routeId: number, alteration: IRouteAlteration, rebuildStatus?: string[], deleting?: boolean) {
    let url = this.routerService.mount(`${this.baseURL}/${routeId}/alteration/preview/async`);
    const params: string[] = [];
    if (alteration.id) params.push(`alteration_id=${alteration.id}`);
    if (rebuildStatus && rebuildStatus.length > 0) params.push(`rebuild_status=${rebuildStatus}`);
    if (deleting) params.push(`deleting=${deleting}`);
    if (params.length > 0) url += `?${params.join('&')}`;
    return this.createText(this.utilsService.snakeCaseBody(alteration), url);
  }

  public deleteAlteration(routeId: number, alterationId: number, processingOptions: IProcessingOptions) {
    const url = this.routerService.mount(`${this.baseURL}/${routeId}/alterations/${alterationId}`);
    return this.delete(url, objectToSnake(processingOptions), HttpResponseType.TEXT);
  }

  public createRestrictions(routeId: number, restriction: IRestrictions) {
    const url = this.routerService.mount(`${this.baseURL}/${routeId}/restrictions`);
    return this.create(objectToSnake(restriction), url);
  }

  public getRestrictions(routeId: number, page?: number, size?: number, searchValue?: string) {
    let url = this.routerService.mount(`${this.baseURL}/${routeId}/restrictions?page=${page}&size=${size}`);
    url += searchValue ? `&search=${encodeURIComponent(searchValue)}` : '';
    return this.read(url);
  }

  public getRestriction(routeId: number, restrictionId: number) {
    const url = this.routerService.mount(`${this.baseURL}/${routeId}/restrictions?id=${restrictionId}`);
    return this.read(url);
  }

  public deleteRestriction(routeId: number, restrictions: IRestrictions[]) {
    const url = this.routerService.mount(`${this.baseURL}/${routeId}/restrictions`);
    return this.delete(url, objectToSnake(restrictions), HttpResponseType.TEXT);
  }

  public changeRestrictionType(routeId: number, type: RestrictionsType, restrictions: IRestrictions[]) {
    const url = this.routerService.mount(`${this.baseURL}/${routeId}/restrictions/type`);
    return this.update(objectToSnake({ type, restrictions }), url, HttpResponseType.TEXT);
  }

}
